import { useAvailableTimes } from "api/api-hooks/use-available-times";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useAllTimeSets } from "hooks/use-all-time-sets";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useMemo } from "react";
import { AvailableTimesSection, AvailableTimesUnit } from "types/available-times";
import { WebRule } from "types/web-rule";

const useSelectedRule = (ruleIdOverride?: number): WebRule | undefined => {
    const state = useBookingStateContext();
    const availableTimes = useAvailableTimes(state.date);
    const { data: webBooking } = useWebBooking();
    const { data: reservation } = useWebBooking();

    const ruleId = (state.times && state.times[0]?.ruleId) || ruleIdOverride;

    let selectedRule = useMemo(() => {
        return availableTimes?.data
            ?.flatMap((item: AvailableTimesUnit) =>
                item.sections.flatMap((section: AvailableTimesSection) => section.timeSets)
            )
            .find((timeSet: WebRule) => timeSet.id === ruleId);
    }, [availableTimes?.data, ruleId]);

    if (reservation && reservation.webBookingRules)
        selectedRule = reservation.webBookingRules?.at(0);

    if (webBooking?.webBookingRules) selectedRule = webBooking.webBookingRules?.at(0);

    return selectedRule;
};

/**
 * Retrieves a list of unique timeSets (WebRules) that the booking is associated with.
 * This is important for activity bookings where a guest can book multiple WebRules.
 *
 * Note:
 * - "timeSets" and "WebRules" are synonyms.
 *
 * @returns {WebRule[]} The selected WebRules for the booking.
 */
const useSelectedRules = (): WebRule[] => {
    const { data: booking } = useWebBooking();
    const allTimeSets = useAllTimeSets();
    const selectedRules = useMemo(() => {
        const ruleIds = [...new Set(booking?.articles?.map((article) => article.ruleId) ?? [])];

        if (ruleIds.length === 0) {
            return [];
        }

        // The rules the backend returned with this booking. Refreshed on every fetch, so
        // they track rule edits made in RMS just like availableTimes does.
        const reservationRulesById = new Map(
            (booking?.webBookingRules ?? []).map((rule) => [rule.id, rule])
        );

        // Date-scoped available times, used when a booked rule is not on the booking payload.
        const allTimeSetsById = new Map((allTimeSets ?? []).map((rule) => [rule.id, rule]));

        const mappedRules = ruleIds
            .map((id) => {
                // Prefer the booking's own rules: they come from the same payload as
                // chargeShouldPayAmount, so whatever decides "is this a prepayment" agrees
                // with the amount rendered next to it. availableTimes is a date-scoped
                // availability list cached for minutes at a time, so reading the payment
                // config from it can disagree with the booking it is describing.
                const fromReservation = reservationRulesById.get(id);
                if (fromReservation) {
                    return fromReservation;
                }

                return allTimeSetsById.get(id);
            })
            .filter((rule): rule is WebRule => Boolean(rule));

        return mappedRules;
    }, [booking?.articles, booking?.webBookingRules, allTimeSets]);

    return selectedRules;
};

export { useSelectedRule, useSelectedRules };
